import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { MessageCircleHeart, Send, Check } from "lucide-react";
import { api } from "@/lib/api";

export function IcebreakerCard() {
  const [data, setData] = useState(null);
  const [answer, setAnswer] = useState("");
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(true);

  const load = () => api.get("/icebreaker/today").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    const a = answer.trim();
    if (!a) return;
    setSaving(true);
    try {
      await api.post("/icebreaker/answer", { answer: a });
      toast.success("Answer posted — it's now in the mix 🔥");
      setAnswer(""); load();
    } catch (e) { toast.error("Could not post your answer"); }
    finally { setSaving(false); }
  };

  if (!data) return null;

  return (
    <motion.div data-testid="icebreaker-card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="pe-card rounded-2xl p-5 mb-6 border border-[#D4AF37]/25">
      <div className="flex items-center justify-between">
        <div className="overline flex items-center gap-2"><MessageCircleHeart size={14} className="text-[#D4AF37]" /> Spark of the day</div>
        <button data-testid="icebreaker-toggle" onClick={() => setOpen((o) => !o)} className="text-xs text-[#A0A0A0] hover:text-white">{open ? "Hide" : "Show"}</button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <h3 className="font-heading text-2xl mt-2 mb-3">{data.prompt}</h3>

            {data.answered ? (
              <div data-testid="icebreaker-answered" className="text-sm text-[#D4AF37] flex items-center gap-2 mb-3"><Check size={15} /> You answered: <span className="text-white/90 italic">"{data.my_answer}"</span></div>
            ) : (
              <div className="flex gap-2 mb-3">
                <input data-testid="icebreaker-input" value={answer} onChange={(e) => setAnswer(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && submit()} maxLength={280}
                  placeholder="Your answer… (everyone can see it)" className="pe-input flex-1 rounded-lg px-4 py-2.5 text-sm" />
                <button data-testid="icebreaker-submit" onClick={submit} disabled={saving || !answer.trim()}
                  className="btn-gold px-4 rounded-lg flex items-center gap-1 disabled:opacity-50"><Send size={15} /></button>
              </div>
            )}

            {data.answers?.length > 0 && (
              <div className="space-y-2 pt-1">
                <div className="text-[11px] text-[#A0A0A0]">{data.answer_count} member{data.answer_count === 1 ? "" : "s"} answered today</div>
                {data.answers.slice(0, 3).map((a) => (
                  <div key={a.user_id} data-testid={`icebreaker-answer-${a.user_id}`} className="flex items-center gap-2 text-sm">
                    <div className="w-7 h-7 rounded-full overflow-hidden bg-[#1A1A1A] flex-shrink-0 border border-[#2A2A2A]">{a.photo && <img src={a.photo} alt="" className="w-full h-full object-cover" />}</div>
                    <span className="text-[#D4AF37] text-xs">{a.name}:</span>
                    <span className="text-white/80 text-xs italic truncate">"{a.answer}"</span>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
